// v49 RRHH — /app/rrhh/acciones
// Bandeja de acciones de personal: registrar, aprobar, rechazar y enviar el
// correo al colaborador (siempre con preview, criterio #9).
//
// La UI no decide quien puede aprobar: el backend valida contra los cargos
// configurados y responde 403 (no sos el aprobador del paso) o 409 (sos el
// sujeto de la accion). Aca solo se muestra el error tal cual.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../config/api';
import { formatFecha } from '../utils/fecha';
import {
  cargarListas, msgError, textoValorAccion,
  inputStyle, btnPrimario, btnSecundario, btnNavy, tarjeta, thStyle, tdStyle
} from '../utils/rrhh';
import { Dato, Badge, Aviso, tonoEstadoAccion } from '../components/RrhhCampos';
import ModalAccionPersonal from '../components/ModalAccionPersonal';
import PreviewCorreo from '../components/PreviewCorreo';

const ESTADOS = ['Borrador', 'Pendiente RRHH', 'Pendiente Gerencia', 'Aplicada', 'Rechazada'];

export default function RrhhAcciones() {
  const navigate = useNavigate();

  const [filas, setFilas]       = useState([]);
  const [listas, setListas]     = useState({});
  const [colaboradores, setColabs] = useState([]);
  const [estado, setEstado]     = useState('');
  const [cargando, setCargando] = useState(true);
  const [abierta, setAbierta]   = useState(null);     // detalle expandido
  const [error, setError]       = useState('');
  const [aviso, setAviso]       = useState('');
  const [procesando, setProcesando] = useState(false);
  const [seleccionado, setSeleccionado] = useState('');   // id elegido en el combo
  const [nuevaPara, setNuevaPara]   = useState(null); // expediente completo para registrar
  const [borrador, setBorrador]     = useState(null); // accion en Borrador que se corrige
  const [correoDe, setCorreoDe]     = useState(null); // accion cuyo correo se previsualiza

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const qs = estado ? `?estado=${encodeURIComponent(estado)}` : '';
      const r = await axios.get(`${API}/rrhh-acciones-personal${qs}`);
      setFilas(r.data || []);
    } catch (e) { setError(msgError(e)); }
    setCargando(false);
  }, [estado]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    (async () => {
      try { setListas(await cargarListas()); } catch (e) { /* opcional */ }
      try {
        const r = await axios.get(`${API}/rrhh-colaboradores?estado=Activo`);
        setColabs(r.data || []);
      } catch (e) { /* opcional */ }
    })();
  }, []);

  const abrirDetalle = async (fila) => {
    setError('');
    if (abierta?.id === fila.id) { setAbierta(null); return; }
    try {
      const r = await axios.get(`${API}/rrhh-acciones-personal?id=${fila.id}`);
      setAbierta(r.data);
    } catch (e) { setError(msgError(e)); }
  };

  // El listado no trae salario ni jefatura (datos sensibles fuera del detalle),
  // asi que el "valor actual" del modal se toma del expediente completo.
  const abrirRegistro = async () => {
    setError('');
    try {
      const r = await axios.get(`${API}/rrhh-colaboradores?id=${seleccionado}`);
      setNuevaPara(r.data);
    } catch (e) { setError(msgError(e)); }
  };

  // Un borrador se envia a aprobacion: ahi arranca la maquina de estados y la
  // accion deja de ser editable para siempre (A10).
  const enviar = async (accion) => {
    if (!window.confirm('Enviar a aprobacion? Despues de esto la accion ya no se puede editar ni eliminar.')) return;
    setProcesando(true); setError(''); setAviso('');
    try {
      const r = await axios.post(`${API}/rrhh-acciones-personal?id=${accion.id}&action=enviar`);
      setAviso(r.data.estado === 'Aplicada'
        ? 'Enviada. Los dos aprobadores eran el sujeto, asi que se aplico de una con constancia de la omision.'
        : `Enviada. Queda en ${r.data.estado}.`);
      setAbierta(null);
      await cargar();
    } catch (e) { setError(msgError(e)); }
    finally { setProcesando(false); }
  };

  const eliminarBorrador = async (accion) => {
    if (!window.confirm('Descartar este borrador?')) return;
    setProcesando(true); setError('');
    try {
      await axios.delete(`${API}/rrhh-acciones-personal?id=${accion.id}`);
      setAviso('Borrador descartado.');
      setAbierta(null);
      await cargar();
    } catch (e) { setError(msgError(e)); }
    finally { setProcesando(false); }
  };

  // Para corregir un borrador hace falta el expediente completo: el modal
  // muestra el valor vigente del campo que se esta cambiando.
  const editarBorrador = async (accion) => {
    setError('');
    try {
      const r = await axios.get(`${API}/rrhh-colaboradores?id=${accion.colaborador_id}`);
      setNuevaPara(r.data);
      setBorrador(accion);
    } catch (e) { setError(msgError(e)); }
  };

  const aprobar = async (accion) => {
    if (!window.confirm(`Aprobar la accion de ${accion.tipo} de ${accion.nombre_completo}?`)) return;
    setProcesando(true); setError(''); setAviso('');
    try {
      const r = await axios.post(`${API}/rrhh-acciones-personal?id=${accion.id}&action=aprobar`);
      setAviso(r.data.estado === 'Aplicada'
        ? 'Aprobada. El cambio ya se aplico al expediente.'
        : `Aprobada. Pasa a ${r.data.estado}.`);
      setAbierta(null);
      await cargar();
    } catch (e) { setError(msgError(e)); }
    finally { setProcesando(false); }
  };

  const rechazar = async (accion) => {
    const motivo = window.prompt('Motivo del rechazo (obligatorio):', '');
    if (motivo === null) return;
    if (!motivo.trim()) { setError('El motivo del rechazo es obligatorio.'); return; }
    setProcesando(true); setError(''); setAviso('');
    try {
      await axios.post(`${API}/rrhh-acciones-personal?id=${accion.id}&action=rechazar`, { motivo });
      setAviso('Accion rechazada. El expediente no cambio.');
      setAbierta(null);
      await cargar();
    } catch (e) { setError(msgError(e)); }
    finally { setProcesando(false); }
  };

  const conteo = (e) => filas.filter(f => f.estado === e).length;
  const pendientes = filas.filter(f => f.estado.startsWith('Pendiente')).length;

  return (
    <div style={{ padding: '24px', fontFamily: 'Segoe UI, sans-serif', maxWidth: '1200px' }}>

      {/* Header */}
      <div style={{ background: '#012E40', borderRadius: '12px', padding: '16px 20px', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ color: '#fff', fontSize: '17px', fontWeight: '700' }}>Acciones de personal</span>
          {!estado && pendientes > 0 && <Badge texto={`${pendientes} pendiente${pendientes !== 1 ? 's' : ''}`} tono="ambar" />}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select value={seleccionado} onChange={e => setSeleccionado(e.target.value)}
            style={{ ...inputStyle, width: 'auto', minWidth: 220 }}>
            <option value="">Registrar accion para...</option>
            {colaboradores.map(c => <option key={c.id} value={c.id}>{c.codigo} · {c.nombre_completo}</option>)}
          </select>
          <button onClick={abrirRegistro} disabled={!seleccionado}
            style={{ ...btnPrimario, opacity: seleccionado ? 1 : 0.5, cursor: seleccionado ? 'pointer' : 'not-allowed' }}>
            + Registrar
          </button>
        </div>
      </div>

      <Aviso texto={error} />
      <Aviso texto={aviso} tipo="ok" />

      {/* Filtro por estado */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {[['', 'Todas'], ...ESTADOS.map(e => [e, e])].map(([val, txt]) => (
          <button key={txt} onClick={() => { setEstado(val); setAbierta(null); }}
            style={{
              padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
              border: `1px solid ${estado === val ? '#012E40' : '#e0e0e0'}`,
              background: estado === val ? '#012E40' : '#fff',
              color: estado === val ? '#fff' : '#555'
            }}>
            {txt}{!estado && val ? ` (${conteo(val)})` : ''}
          </button>
        ))}
      </div>

      {/* Bandeja */}
      <div style={{ ...tarjeta, overflow: 'hidden' }}>
        {cargando ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}>Cargando...</div>
        ) : filas.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#bbb' }}>No hay acciones en este estado.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#f8f9fa' }}>
                {['Registrada', 'Codigo', 'Colaborador', 'Tipo', 'Rige desde', 'Estado', ''].map(h => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filas.map(a => (
                <React.Fragment key={a.id}>
                  <tr onClick={() => abrirDetalle(a)} style={{ borderBottom: '1px solid #f3f3f3', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={tdStyle}>{formatFecha(a.fecha_registro)}</td>
                    <td style={{ ...tdStyle, color: '#012E40', fontWeight: 600 }}>{a.codigo}</td>
                    <td style={{ ...tdStyle, color: '#1a1a1a', fontWeight: 500 }}>{a.nombre_completo}</td>
                    <td style={tdStyle}>{a.tipo}</td>
                    <td style={tdStyle}>{formatFecha(a.rige_desde)}</td>
                    <td style={tdStyle}><Badge texto={a.estado} tono={tonoEstadoAccion(a.estado)} /></td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: '#aaa' }}>{abierta?.id === a.id ? '▲' : '▼'}</td>
                  </tr>

                  {abierta?.id === a.id && (
                    <tr>
                      <td colSpan={7} style={{ background: '#fafbfc', padding: '16px', borderBottom: '1px solid #eee' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px', marginBottom: 14 }}>
                          <Dato label="Valor anterior" valor={textoValorAccion(abierta.tipo, abierta.valor_anterior, listas, colaboradores)} />
                          <Dato label="Valor nuevo" valor={textoValorAccion(abierta.tipo, abierta.valor_nuevo, listas, colaboradores)} />
                          <Dato label="Motivo de la solicitud" valor={abierta.motivo} />
                          <Dato label="Registrada por" valor={abierta.registrada_por} />
                          <Dato label="Aprobacion RRHH" valor={abierta.aprob_rrhh_omitida
                            ? `Omitida — ${abierta.motivo_omision}`
                            : (abierta.aprob_rrhh_por ? `${abierta.aprob_rrhh_por} · ${formatFecha(abierta.aprob_rrhh_fecha)}` : 'Pendiente')} />
                          <Dato label="Aprobacion Gerencia" valor={abierta.aprob_gerencia_omitida
                            ? `Omitida — ${abierta.motivo_omision}`
                            : (abierta.aprob_gerencia_por ? `${abierta.aprob_gerencia_por} · ${formatFecha(abierta.aprob_gerencia_fecha)}` : 'Pendiente')} />
                          <Dato label="Aplicada" valor={formatFecha(abierta.fecha_aplicacion)} />
                          {/* El motivo del rechazo tiene columna propia: no pisa
                              el motivo por el que se pidio el cambio (D-1). */}
                          {abierta.motivo_rechazo && <Dato label="Motivo del rechazo" ancho={2} valor={abierta.motivo_rechazo} />}
                          <Dato label="Correo al colaborador" valor={abierta.correo_enviado_fecha ? formatFecha(abierta.correo_enviado_fecha) : 'No enviado'} />
                        </div>

                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <button onClick={() => navigate(`/app/rrhh/expedientes/${abierta.colaborador_id}`)} style={btnSecundario}>
                            Ver expediente
                          </button>

                          {abierta.estado === 'Borrador' && (
                            <>
                              <button onClick={() => enviar(abierta)} disabled={procesando}
                                style={{ ...btnNavy, opacity: procesando ? 0.6 : 1 }}>
                                Enviar a aprobacion
                              </button>
                              <button onClick={() => editarBorrador(abierta)} disabled={procesando} style={btnSecundario}>
                                Corregir
                              </button>
                              <button onClick={() => eliminarBorrador(abierta)} disabled={procesando}
                                style={{ ...btnSecundario, color: '#A32D2D', borderColor: '#fcc' }}>
                                Descartar
                              </button>
                            </>
                          )}

                          {abierta.estado.startsWith('Pendiente') && (
                            <>
                              <button onClick={() => aprobar(abierta)} disabled={procesando}
                                style={{ ...btnNavy, opacity: procesando ? 0.6 : 1 }}>
                                Aprobar ({abierta.estado === 'Pendiente RRHH' ? 'RRHH' : 'Gerencia'})
                              </button>
                              <button onClick={() => rechazar(abierta)} disabled={procesando}
                                style={{ ...btnSecundario, color: '#A32D2D', borderColor: '#fcc' }}>
                                Rechazar
                              </button>
                            </>
                          )}

                          {abierta.estado === 'Aplicada' && (
                            <button onClick={() => setCorreoDe(abierta)} style={btnPrimario}>
                              {abierta.correo_enviado_fecha ? 'Reenviar correo al colaborador' : 'Enviar correo al colaborador'}
                            </button>
                          )}
                        </div>

                        {abierta.estado === 'Borrador' && (
                          <div style={{ fontSize: 11, color: '#888', marginTop: 10 }}>
                            Todavia no cambia nada en el expediente ni hay aprobaciones corriendo. Al enviarla ya no se edita.
                          </div>
                        )}
                        {abierta.estado.startsWith('Pendiente') && (
                          <div style={{ fontSize: 11, color: '#888', marginTop: 10 }}>
                            Solo el cargo aprobador configurado para este paso puede resolverla, y nunca sobre su propia accion.
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Registrar accion nueva o corregir un borrador */}
      {nuevaPara && (
        <ModalAccionPersonal
          colaborador={nuevaPara} listas={listas} colaboradores={colaboradores}
          borrador={borrador} tipoInicial={borrador?.tipo || 'Puesto'}
          onClose={() => { setNuevaPara(null); setBorrador(null); }}
          onGuardado={() => {
            setNuevaPara(null);
            setBorrador(null);
            setSeleccionado('');
            setAbierta(null);
            setAviso('Borrador guardado. Revisalo y envialo a aprobacion cuando este listo.');
            cargar();
          }}
        />
      )}

      {/* Preview del correo antes de enviarlo (criterio #9) */}
      {correoDe && (
        <PreviewCorreo
          titulo={`Accion de personal: ${correoDe.tipo}`}
          previewUrl={`${API}/rrhh-acciones-personal?id=${correoDe.id}&action=preview-correo`}
          sendUrl={`${API}/rrhh-acciones-personal?id=${correoDe.id}&action=enviar-correo`}
          enviarLabel="Enviar al colaborador"
          onClose={() => setCorreoDe(null)}
          onSent={() => {
            setCorreoDe(null);
            setAviso('Correo enviado al colaborador.');
            setAbierta(null);
            cargar();
          }}
        />
      )}
    </div>
  );
}
